import React, { useState } from 'react';
import { collection, doc, setDoc, serverTimestamp } from 'firebase/firestore';
import { db } from '../firebase';
import { Upload, FileText, CheckCircle, AlertCircle, X } from 'lucide-react';
import './CSVUpload.css';

const CSVUpload = ({ onClose, onUploadSuccess }) => {
  const [file, setFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [uploadStatus, setUploadStatus] = useState('');
  const [uploadedProducts, setUploadedProducts] = useState([]);
  const [errors, setErrors] = useState([]);
  const [preview, setPreview] = useState([]);

  const handleFileSelect = (e) => {
    const selectedFile = e.target.files[0];
    if (selectedFile && selectedFile.type === 'text/csv') {
      setFile(selectedFile);
      setUploadStatus('');
      setErrors([]);
      setUploadedProducts([]);
      setPreview([]);
      parseCSV(selectedFile);
    } else {
      setUploadStatus('error');
      setErrors(['Please select a valid CSV file']);
    }
  };

  const parseCSV = (file) => {
    const reader = new FileReader();
    reader.onload = (e) => {
      const text = e.target.result;
      const lines = text.split('\n').filter(line => line.trim());
      
      if (lines.length < 2) {
        setUploadStatus('error');
        setErrors(['CSV file must have at least a header row and one data row']);
        return;
      }

      const headers = lines[0].split(',').map(h => h.trim().replace(/"/g, ''));
      const expectedHeaders = ['SKU', 'Name', 'Category', 'Price', 'Description', 'Image', 'Stock', 'Status'];
      
      // Validate headers
      const missingHeaders = expectedHeaders.filter(header => !headers.includes(header));
      if (missingHeaders.length > 0) {
        setUploadStatus('error');
        setErrors([`Missing required columns: ${missingHeaders.join(', ')}`]);
        return;
      }

      // Parse data rows
      const products = [];
      const parseErrors = [];

      for (let i = 1; i < lines.length; i++) {
        const values = lines[i].split(',').map(v => v.trim().replace(/"/g, ''));
        
        if (values.length !== headers.length) {
          parseErrors.push(`Row ${i + 1}: Incorrect number of columns`);
          continue;
        }

        const product = {};
        headers.forEach((header, index) => {
          product[header] = values[index];
        });

        // Validate required fields
        if (!product.SKU || !product.Name || !product.Category || !product.Price) {
          parseErrors.push(`Row ${i + 1}: Missing required fields (SKU, Name, Category, Price)`);
          continue;
        }

        // Convert price to number
        const price = parseFloat(product.Price);
        if (isNaN(price) || price <= 0) {
          parseErrors.push(`Row ${i + 1}: Invalid price "${product.Price}"`);
          continue;
        }

        // Convert stock to number
        const stock = parseInt(product.Stock) || 0;
        if (stock < 0) {
          parseErrors.push(`Row ${i + 1}: Stock cannot be negative`);
          continue;
        }

        // Format product for Firestore
        products.push({
          id: product.SKU,
          name: product.Name,
          category: product.Category,
          price: price,
          description: product.Description || '',
          image: product.Image || '',
          stock: stock,
          status: product.Status || 'Active',
          createdAt: serverTimestamp(),
          updatedAt: serverTimestamp()
        });
      }

      if (parseErrors.length > 0) {
        setErrors(parseErrors);
      }

      setPreview(products.slice(0, 5)); // Show first 5 products as preview
    };
    reader.readAsText(file);
  };

  const handleUpload = async () => {
    if (!file || preview.length === 0) return;

    setUploading(true);
    setUploadStatus('');
    setErrors([]);
    setUploadedProducts([]);

    try {
      const reader = new FileReader();
      reader.onload = async (e) => {
        const text = e.target.result;
        const lines = text.split('\n').filter(line => line.trim());
        const headers = lines[0].split(',').map(h => h.trim().replace(/"/g, ''));
        
        const products = [];
        const uploadErrors = [];
        let successCount = 0;

        // Process each row
        for (let i = 1; i < lines.length; i++) {
          const values = lines[i].split(',').map(v => v.trim().replace(/"/g, ''));
          
          if (values.length !== headers.length) continue;

          const product = {};
          headers.forEach((header, index) => {
            product[header] = values[index];
          });

          try {
            // Save to Firestore
            const productData = {
              id: product.SKU,
              name: product.Name,
              category: product.Category,
              price: parseFloat(product.Price),
              description: product.Description || '',
              image: product.Image || '',
              stock: parseInt(product.Stock) || 0,
              status: product.Status || 'Active',
              createdAt: serverTimestamp(),
              updatedAt: serverTimestamp()
            };

            await setDoc(doc(db, 'products', product.SKU), productData);
            products.push(productData);
            successCount++;
          } catch (error) {
            uploadErrors.push(`Row ${i + 1} (${product.SKU}): ${error.message}`);
          }
        }

        if (uploadErrors.length > 0) {
          setErrors(uploadErrors);
          setUploadStatus('partial');
        } else {
          setUploadStatus('success');
        }

        setUploadedProducts(products);
        setUploading(false);
        
        if (successCount > 0) {
          onUploadSuccess && onUploadSuccess(products);
        }
      };
      reader.readAsText(file);
    } catch (error) {
      console.error('Upload error:', error);
      setUploadStatus('error');
      setErrors([`Upload failed: ${error.message}`]);
      setUploading(false);
    }
  };

  const resetUpload = () => {
    setFile(null);
    setUploadStatus('');
    setErrors([]);
    setUploadedProducts([]);
    setPreview([]);
  };

  return (
    <div className="csv-upload-overlay" onClick={onClose}>
      <div className="csv-upload-panel" onClick={(e) => e.stopPropagation()}>
        <div className="csv-upload-header">
          <h2><Upload size={24} /> Import Products from CSV</h2>
          <button className="close-btn" onClick={onClose}>
            <X size={20} />
          </button>
        </div>

        <div className="csv-upload-content">
          {/* File Selection */}
          <div className="upload-section">
            <div className="upload-area">
              <input
                type="file"
                id="csv-file"
                accept=".csv"
                onChange={handleFileSelect}
                style={{ display: 'none' }}
              />
              <label htmlFor="csv-file" className="upload-label">
                <FileText size={48} />
                <h3>Choose CSV File</h3>
                <p>Select your products CSV file to import</p>
                <span className="file-types">.CSV files only</span>
              </label>
            </div>

            {file && (
              <div className="selected-file">
                <FileText size={20} />
                <span>{file.name}</span>
                <button className="remove-file" onClick={resetUpload}>
                  <X size={16} />
                </button>
              </div>
            )}
          </div>

          {/* Preview */}
          {preview.length > 0 && (
            <div className="preview-section">
              <h3>Preview (First 5 products)</h3>
              <div className="preview-table">
                <table>
                  <thead>
                    <tr>
                      <th>SKU</th>
                      <th>Name</th>
                      <th>Category</th>
                      <th>Price</th>
                      <th>Stock</th>
                    </tr>
                  </thead>
                  <tbody>
                    {preview.map((product, index) => (
                      <tr key={index}>
                        <td>{product.id}</td>
                        <td>{product.name}</td>
                        <td>{product.category}</td>
                        <td>${product.price.toFixed(2)}</td>
                        <td>{product.stock}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <p className="preview-note">
                {preview.length} products shown in preview. 
                Total products in file will be processed during upload.
              </p>
            </div>
          )}

          {/* Errors */}
          {errors.length > 0 && (
            <div className="errors-section">
              <h3><AlertCircle size={20} /> Errors Found</h3>
              <div className="error-list">
                {errors.map((error, index) => (
                  <div key={index} className="error-item">
                    {error}
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Status Messages */}
          {uploadStatus === 'success' && (
            <div className="status-message success">
              <CheckCircle size={20} />
              <span>Successfully uploaded {uploadedProducts.length} products!</span>
            </div>
          )}

          {uploadStatus === 'partial' && (
            <div className="status-message warning">
              <AlertCircle size={20} />
              <span>Uploaded {uploadedProducts.length} products with {errors.length} errors</span>
            </div>
          )}

          {uploadStatus === 'error' && (
            <div className="status-message error">
              <AlertCircle size={20} />
              <span>Upload failed. Please check the errors above.</span>
            </div>
          )}

          {/* Upload Button */}
          {file && preview.length > 0 && uploadStatus !== 'success' && (
            <div className="upload-actions">
              <button
                className="upload-btn"
                onClick={handleUpload}
                disabled={uploading}
              >
                {uploading ? (
                  <>
                    <div className="spinner"></div>
                    Uploading...
                  </>
                ) : (
                  <>
                    <Upload size={20} />
                    Upload {preview.length}+ Products
                  </>
                )}
              </button>
            </div>
          )}

          {/* Instructions */}
          <div className="instructions">
            <h3>CSV Format Instructions</h3>
            <p>Your CSV file must include these columns in this exact order:</p>
            <div className="csv-format">
              <code>SKU,Name,Category,Price,Description,Image,Stock,Status</code>
            </div>
            <ul>
              <li><strong>SKU:</strong> Unique product identifier</li>
              <li><strong>Name:</strong> Product name</li>
              <li><strong>Category:</strong> Must match your category names exactly</li>
              <li><strong>Price:</strong> Numeric price (e.g., 25.99)</li>
              <li><strong>Description:</strong> Product description</li>
              <li><strong>Image:</strong> Image filename (can be empty)</li>
              <li><strong>Stock:</strong> Numeric stock quantity</li>
              <li><strong>Status:</strong> Active or Inactive</li>
            </ul>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CSVUpload;
